import { CircularProgress } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import './ForgotPage.scss';

export const ForgotPasswordPage: React.FC = () => {
  const { forgotPassword, isAuthenticated, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    setEmailError('');
    if (error) clearError();
    // Deliberately scoped to `email` only — this clears the error as the user
    // edits the field. Adding `error`/`clearError` (as the linter suggests)
    // would also re-run this effect the instant `error` is *set* by a failed
    // submit, immediately clearing the message we just showed the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Particle animation — same as login page
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    interface P { x: number; y: number; r: number; dx: number; dy: number; o: number; }
    const rand = (a: number, b: number) => Math.random() * (b - a) + a;
    let particles: P[] = [];
    let raf = 0;

    const resize = () => {
      const parent = canvas.parentElement!;
      const rect = parent.getBoundingClientRect();
      canvas.width  = rect.width  || window.innerWidth;
      canvas.height = rect.height || window.innerHeight;
      particles = Array.from({ length: 40 }, () => ({
        x: rand(0, canvas.width), y: rand(0, canvas.height),
        r: rand(1, 3.5), dx: rand(-0.3, 0.3), dy: rand(-0.25, 0.25), o: rand(0.10, 0.45),
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${p.o})`;
        ctx.fill();
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width)  p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      }
      raf = requestAnimationFrame(draw);
    };

    requestAnimationFrame(() => { resize(); draw(); });
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setEmailError('Email is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailError('Please enter a valid email address'); return; }
    setIsSubmitting(true);
    try {
      await forgotPassword({ email });
      setIsSubmitted(true);
    } catch {
      // error shown via auth context
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fp-loading">
        <CircularProgress style={{ color: '#2B9BA5' }} />
      </div>
    );
  }

  return (
    <div className="fp-root" data-testid="forgot-password-page">
      <link href="https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0&display=swap" rel="stylesheet" />

      {/* Left — hero image */}
      <div className="fp-left">
        <img src="/assets/login/hrms-hero.png" alt="HRMS Hero" className="fp-hero-img" />
      </div>

      {/* Right — teal + particles + card */}
      <div className="fp-right">
        <canvas ref={canvasRef} className="fp-particles-canvas" />

        <div className="fp-card">
          {!isSubmitted ? (
            <>
              <div className="fp-header">
                <h2 className="fp-title">
                  {type === 'generate' ? 'Forgot Password' : 'Forgot Password'}
                </h2>
                <p className="fp-subtitle">
                  Enter your email and we'll send you a link to{' '}
                  {type === 'generate' ? 'generate' : 'reset'} your password.
                </p>
              </div>

              {error && (
                <div className="fp-alert-error" data-testid="forgot-password-error-alert">
                  <span className="material-symbols-outlined">error</span>
                  <span>{error}</span>
                </div>
              )}

              <form className="fp-form" onSubmit={handleSubmit} noValidate>
                <div className={`fp-field${emailError ? ' fp-field-error' : ''}`}>
                  <label className="fp-label" htmlFor="fp-email">Email address</label>
                  <div className="fp-input-wrap">
                    <span className="material-symbols-outlined fp-input-icon">mail</span>
                    <input
                      id="fp-email"
                      className="fp-input"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      autoComplete="email"
                      autoFocus
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                  {emailError && <span className="fp-field-msg" data-testid="forgot-password-email-error">{emailError}</span>}
                </div>

                <button type="submit" className="fp-submit" disabled={isSubmitting} data-testid="forgot-password-submit-button">
                  {isSubmitting
                    ? <CircularProgress size={20} style={{ color: '#fff' }} />
                    : `Send ${type === 'generate' ? 'Generate' : 'Reset'} Link`}
                </button>

                <div className="fp-back-row">
                  <Link className="fp-back-link" to="/auth/login" data-testid="forgot-password-back-login-link">
                    <span className="material-symbols-outlined">arrow_back</span>
                    Back to Login
                  </Link>
                </div>
              </form>
            </>
          ) : (
            <div className="fp-success" data-testid="forgot-password-success">
              <span className="material-symbols-outlined fp-success-icon">mark_email_read</span>
              <h2 className="fp-title">Check Your Email</h2>
              <p className="fp-subtitle">
                We've sent a link to <strong>{email}</strong>.<br />
                The link will expire in 1 hour.
              </p>
              <div className="fp-success-actions">
                <button
                  className="fp-submit fp-submit-outline"
                  onClick={() => setIsSubmitted(false)}
                  data-testid="forgot-password-retry-button"
                >
                  Try Another Email
                </button>
                <Link className="fp-submit" to="/auth/login" data-testid="forgot-password-success-login-link">
                  Back to Login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
